import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "@/App";
import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { MDRBadge } from "@/components/MDRBadge";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  accreditedStructuresData,
  specialistAssignmentsData,
  reportingActivityData,
  pharmaciesData,
  getStructureById
} from "@/data/platformData";
import { servicesData, doctorsData } from "@/data/mockData";
import {
  ArrowLeft, ArrowRight, Hospital, Stethoscope, Clock, Award,
  CheckCircle2, FileSignature, Users, Building2, MapPin, Mail, Phone, AlertOctagon,
  Trophy, Timer, Sparkles
} from "lucide-react";

// Operator works at ph1 (mock — consistent with rest of app)
const OPERATOR_PHARMACY_ID = "ph1";

const formatHours = (ms) => {
  if (!ms || ms <= 0) return "—";
  const h = ms / 3600000;
  return h < 24 ? `${h.toFixed(1)}h` : `${(h / 24).toFixed(1)}g`;
};

export const ReportingRoutingPage = () => {
  const navigate = useNavigate();
  const { selectedPatient, selectedServices, setSessionResults, sessionResults } = useAppContext();

  // Derive services requiring report
  const reportingServices = useMemo(() => {
    return selectedServices
      .map(sid => servicesData.find(s => s.id === sid))
      .filter(s => s && s.requiresReport);
  }, [selectedServices]);

  const reportingServiceIds = reportingServices.map(s => s.id);

  // Eligible structures = associated with pharmacy AND can handle ALL reporting services
  const eligibleStructures = useMemo(() => {
    const pharmacy = pharmaciesData.find(p => p.id === OPERATOR_PHARMACY_ID);
    if (!pharmacy) return [];
    return accreditedStructuresData
      .filter(s => pharmacy.accreditedStructureIds.includes(s.id))
      .filter(s => reportingServiceIds.every(sid => s.handledServices.includes(sid)));
  }, [reportingServiceIds]);

  // Structure metrics: signed count, avg time-to-report (ms)
  const structureMetrics = useMemo(() => {
    const map = {};
    eligibleStructures.forEach(s => {
      const acts = reportingActivityData.filter(a => a.structureId === s.id);
      const signed = acts.filter(a => a.deliveredAt);
      const avgMs = signed.length
        ? signed.reduce((acc, a) => acc + (new Date(a.deliveredAt) - new Date(a.receivedAt)), 0) / signed.length
        : null;
      map[s.id] = {
        total: acts.length,
        signedCount: signed.length,
        avgMs,
        productivityScore: signed.length * 100 - (avgMs ? avgMs / 3600000 : 0)
      };
    });
    return map;
  }, [eligibleStructures]);

  // Most productive structure (default selection)
  const mostProductiveId = useMemo(() => {
    return eligibleStructures
      .slice()
      .sort((a, b) => (structureMetrics[b.id]?.productivityScore || 0) - (structureMetrics[a.id]?.productivityScore || 0))[0]?.id;
  }, [eligibleStructures, structureMetrics]);

  const [selectedStructureId, setSelectedStructureId] = useState(mostProductiveId);
  const [selectedSpecialistId, setSelectedSpecialistId] = useState(null);

  // Eligible specialists for selected structure (must handle ALL reporting services)
  const specialistsForStructure = (structureId) => {
    return doctorsData
      .map(d => {
        const assignment = specialistAssignmentsData.find(a => a.specialistId === d.id && a.structureId === structureId);
        if (!assignment) return null;
        const handlesAll = reportingServiceIds.every(sid => assignment.services.includes(sid));
        if (!handlesAll) return null;
        const acts = reportingActivityData.filter(a => a.structureId === structureId && a.specialistId === d.id);
        const signed = acts.filter(a => a.deliveredAt);
        const avgMs = signed.length
          ? signed.reduce((acc, a) => acc + (new Date(a.deliveredAt) - new Date(a.receivedAt)), 0) / signed.length
          : null;
        return { ...d, signedCount: signed.length, avgMs, services: assignment.services };
      })
      .filter(Boolean);
  };

  const handleConfirm = () => {
    const structure = getStructureById(selectedStructureId);
    const specialist = selectedSpecialistId ? doctorsData.find(d => d.id === selectedSpecialistId) : null;
    setSessionResults({
      ...sessionResults,
      reporting: {
        structureId: selectedStructureId,
        structureName: structure?.name,
        specialistId: selectedSpecialistId,
        specialistName: specialist?.name,
        services: reportingServiceIds,
        routedAt: new Date().toISOString()
      }
    });
    toast.success("Routing confermato", {
      description: `${structure?.name}${specialist ? ` · ${specialist.name}` : " (qualsiasi specialista)"}`
    });
    navigate("/export");
  };

  // Empty state - no reporting services
  if (reportingServices.length === 0) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header title="Routing refertazione" />
        <main className="flex-1 px-4 pb-6 md:px-6 flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="p-8 text-center space-y-4">
              <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
              <p className="font-medium">Nessun esame richiede refertazione medica</p>
              <p className="text-sm text-muted-foreground">Procedi all'esportazione dei risultati.</p>
              <Button variant="premium" onClick={() => navigate("/export")} className="gap-2">
                Esporta risultati <ArrowRight className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Empty state - no eligible structure
  if (eligibleStructures.length === 0) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header title="Routing refertazione" />
        <main className="flex-1 px-4 pb-6 md:px-6 flex items-center justify-center">
          <Card className="max-w-lg border-rose-200">
            <CardContent className="p-8 text-center space-y-4">
              <AlertOctagon className="h-12 w-12 text-rose-600 mx-auto" />
              <p className="font-medium">Nessuna struttura accreditata disponibile</p>
              <p className="text-sm text-muted-foreground">
                Le prestazioni selezionate ({reportingServices.map(s => s.name).join(", ")}) non possono essere refertate da nessuna struttura convenzionata con la farmacia. Contattare il responsabile per attivare nuove convenzioni.
              </p>
              <Button variant="outline" onClick={() => navigate("/results")} className="gap-2">
                <ArrowLeft className="h-4 w-4" /> Torna ai risultati
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header title="Routing refertazione" />

      <main className="flex-1 px-4 pb-32 md:px-6 pt-4">
        <div className="mx-auto max-w-4xl space-y-6">
          {/* Header */}
          <div>
            <Button variant="ghost" size="sm" className="-ml-2 mb-2" onClick={() => navigate("/results")}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Risultati
            </Button>
            <div className="flex items-center gap-2 mb-1">
              <Hospital className="h-4 w-4 text-cyan-600" />
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Routing refertazione</span>
              <MDRBadge variant="medical" size="sm" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Seleziona struttura di refertazione</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Paziente: <span className="font-medium">{selectedPatient?.name}</span> · {reportingServices.length} prestazioni da refertare
            </p>
          </div>

          {/* Reporting services summary */}
          <Card className="bg-cyan-50/40 border-cyan-200">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wider text-cyan-700 font-semibold mb-2">Prestazioni da refertare</p>
              <div className="flex flex-wrap gap-1.5">
                {reportingServices.map(s => (
                  <Badge key={s.id} variant="outline" className="bg-white border-cyan-300 text-cyan-800">
                    <FileSignature className="h-3 w-3 mr-1" />
                    {s.name}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Structure cards */}
          <div className="space-y-3">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
              {eligibleStructures.length} {eligibleStructures.length === 1 ? "struttura accreditata" : "strutture accreditate"} idonee
            </p>

            {eligibleStructures
              .slice()
              .sort((a, b) => (structureMetrics[b.id]?.productivityScore || 0) - (structureMetrics[a.id]?.productivityScore || 0))
              .map((s, idx) => {
                const m = structureMetrics[s.id];
                const isSelected = selectedStructureId === s.id;
                const isMostProductive = s.id === mostProductiveId;
                const specialists = specialistsForStructure(s.id);

                return (
                  <Card
                    key={s.id}
                    className={cn(
                      "transition-all cursor-pointer",
                      isSelected && "ring-2 ring-cyan-500 shadow-md border-cyan-300",
                      isMostProductive && !isSelected && "border-amber-200"
                    )}
                    onClick={() => { setSelectedStructureId(s.id); setSelectedSpecialistId(null); }}
                    data-testid={`structure-card-${s.id}`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          <div className={cn(
                            "h-10 w-10 rounded-lg flex items-center justify-center shrink-0",
                            isSelected ? "bg-cyan-500 text-white" : "bg-cyan-100 text-cyan-700"
                          )}>
                            {isSelected ? <CheckCircle2 className="h-5 w-5" /> : <Hospital className="h-5 w-5" />}
                          </div>
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <CardTitle className="text-base">{s.name}</CardTitle>
                              {isMostProductive && (
                                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 gap-1 text-[10px]">
                                  <Trophy className="h-3 w-3" /> Più produttiva
                                </Badge>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mt-1">
                              <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {s.address}</span>
                              <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {s.phone}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Metrics row */}
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="p-2 rounded-lg bg-muted/40">
                          <div className="flex items-center justify-center gap-1 text-emerald-700">
                            <FileSignature className="h-3 w-3" />
                            <p className="text-lg font-bold">{m.signedCount}</p>
                          </div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Refertati</p>
                        </div>
                        <div className="p-2 rounded-lg bg-muted/40">
                          <div className="flex items-center justify-center gap-1 text-sky-700">
                            <Timer className="h-3 w-3" />
                            <p className="text-lg font-bold">{formatHours(m.avgMs)}</p>
                          </div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Tempo medio</p>
                        </div>
                        <div className="p-2 rounded-lg bg-muted/40">
                          <div className="flex items-center justify-center gap-1 text-indigo-700">
                            <Users className="h-3 w-3" />
                            <p className="text-lg font-bold">{specialists.length}</p>
                          </div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Specialisti</p>
                        </div>
                      </div>

                      {/* Specialists list (only when card selected) */}
                      {isSelected && (
                        <div className="space-y-2 pt-2 border-t" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-between">
                            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                              Specialisti disponibili
                            </p>
                            {selectedSpecialistId && (
                              <Button variant="ghost" size="sm" className="h-6 text-[11px]" onClick={() => setSelectedSpecialistId(null)}>
                                Reset selezione
                              </Button>
                            )}
                          </div>
                          {specialists.length === 0 && (
                            <p className="text-xs text-muted-foreground italic">Nessuno specialista abilitato a tutte le prestazioni richieste in questa struttura. Verrà assegnato automaticamente.</p>
                          )}
                          {specialists.map(sp => {
                            const isSpSel = selectedSpecialistId === sp.id;
                            return (
                              <button
                                key={sp.id}
                                onClick={() => setSelectedSpecialistId(isSpSel ? null : sp.id)}
                                className={cn(
                                  "w-full text-left p-3 rounded-lg border transition-all flex items-center gap-3",
                                  isSpSel
                                    ? "bg-cyan-100 border-cyan-400 ring-1 ring-cyan-400"
                                    : "bg-card hover:bg-muted/50"
                                )}
                                data-testid={`specialist-pick-${sp.id}`}
                              >
                                <Avatar className="h-9 w-9 border">
                                  <AvatarFallback className="bg-cyan-50 text-cyan-700 font-semibold text-xs">
                                    {sp.name.replace(/^Dr\.ssa?|^Dr\./, "").trim().split(" ").map(n => n[0]).join("").slice(0, 2)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className="font-medium text-sm truncate">{sp.name}</p>
                                    {isSpSel && <CheckCircle2 className="h-4 w-4 text-cyan-600 shrink-0" />}
                                  </div>
                                  <p className="text-xs text-muted-foreground truncate">{sp.specialization}</p>
                                </div>
                                <div className="text-right shrink-0">
                                  <p className="text-xs font-semibold">{sp.signedCount} <span className="text-muted-foreground font-normal">referti</span></p>
                                  <p className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5">
                                    <Timer className="h-2.5 w-2.5" /> {formatHours(sp.avgMs)}
                                  </p>
                                </div>
                              </button>
                            );
                          })}
                          {specialists.length > 0 && !selectedSpecialistId && (
                            <p className="text-[11px] text-muted-foreground italic flex items-center gap-1 mt-1">
                              <Sparkles className="h-3 w-3" />
                              Lascia non selezionato per assegnazione automatica
                            </p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
          </div>
        </div>
      </main>

      {/* Sticky footer */}
      <div className="fixed bottom-0 left-0 right-0 border-t bg-background shadow-lg">
        <div className="mx-auto max-w-4xl px-4 py-4 md:px-6 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm">
            {selectedStructureId ? (
              <div>
                <p className="font-medium">{getStructureById(selectedStructureId)?.name}</p>
                <p className="text-xs text-muted-foreground">
                  {selectedSpecialistId
                    ? doctorsData.find(d => d.id === selectedSpecialistId)?.name
                    : "Specialista: assegnazione automatica"}
                </p>
              </div>
            ) : (
              <p className="text-muted-foreground">Seleziona una struttura</p>
            )}
          </div>
          <Button
            variant="premium"
            size="lg"
            onClick={handleConfirm}
            disabled={!selectedStructureId}
            className="gap-2"
            data-testid="reporting-routing-confirm-btn"
          >
            Conferma e procedi
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReportingRoutingPage;
